import { type Address } from "viem";

/* ═══════════════════════════════════════════════════════════════════════════ */
/* DJNN REWARD SYSTEM — ForgedDistributorV5 CONFIG                           */
/* ═══════════════════════════════════════════════════════════════════════════ */

export const DJNN_TOKEN_ADDRESS =
  "0xEEAE625913aEE341eCCb5Fbe34D5e4FC3a1A3397" as Address;

/* ─── Logo helpers ────────────────────────────────────────────────────────── */

const TW_ETH = (addr: string) =>
  `https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/ethereum/assets/${addr}/logo.png`;

/* ─── Reward token metadata ──────────────────────────────────────────────── */

export interface RewardTokenMeta {
  symbol: string;
  name: string;
  decimals: number;
  logoUrl: string;
  color: string;
  isNative: boolean;
  address: Address;
}

/** Zero-address sentinel for the native gas token (JASMY) reward slot */
export const NATIVE_REWARD_ADDRESS =
  "0x0000000000000000000000000000000000000000" as Address;

export const REWARD_TOKEN_REGISTRY: Record<string, RewardTokenMeta> = {
  /* Native JASMY — the gas token on JasmyChain */
  [NATIVE_REWARD_ADDRESS.toLowerCase()]: {
    symbol: "JASMY",
    name: "JASMY (Native)",
    decimals: 18,
    logoUrl: "/jasmy.svg",
    color: "#39E6FF",
    isNative: true,
    address: NATIVE_REWARD_ADDRESS,
  },
  /* pJASMY — PulseChain bridged JASMY */
  ["0x6d5Ce519478088127389c3eBe6f312736d027484".toLowerCase()]: {
    symbol: "pJASMY",
    name: "JASMY (PulseChain)",
    decimals: 18,
    logoUrl: "/jasmy.svg",
    color: "#F7931A",
    isNative: false,
    address: "0x6d5Ce519478088127389c3eBe6f312736d027484" as Address,
  },
  /* pDAI — PulseChain bridged DAI */
  ["0xB719759640e32F1aDD8F9F67c303DDD4f10DC3fa".toLowerCase()]: {
    symbol: "pDAI",
    name: "DAI (PulseChain)",
    decimals: 18,
    logoUrl: TW_ETH("0x6B175474E89094C44Da98b954EedeAC495271d0F"),
    color: "#F5AC37",
    isNative: false,
    address: "0xB719759640e32F1aDD8F9F67c303DDD4f10DC3fa" as Address,
  },
  /* pGYEN — PulseChain bridged GYEN (6 decimals!) */
  ["0x6E218Ab12106473E013AD5f4836220825A55CA45".toLowerCase()]: {
    symbol: "pGYEN",
    name: "GYEN (PulseChain)",
    decimals: 6,
    logoUrl: TW_ETH("0xC08512927D12348F6620a698105e1BAac6EcD911"),
    color: "#E8B65A",
    isNative: false,
    address: "0x6E218Ab12106473E013AD5f4836220825A55CA45" as Address,
  },
  /* bWBTC — PulseChain bridged WBTC (8 decimals) */
  ["0x7D68dc061bF89B00236950d288155767459e40Fb".toLowerCase()]: {
    symbol: "bWBTC",
    name: "WBTC (PulseChain)",
    decimals: 8,
    logoUrl: TW_ETH("0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599"),
    color: "#F7931A",
    isNative: false,
    address: "0x7D68dc061bF89B00236950d288155767459e40Fb" as Address,
  },
};

/**
 * Lookup reward token metadata by address.
 * Falls back to sensible defaults for unknown tokens.
 */
export function getRewardTokenMeta(address: Address): RewardTokenMeta {
  const key = address.toLowerCase();
  if (REWARD_TOKEN_REGISTRY[key]) return REWARD_TOKEN_REGISTRY[key];
  return {
    symbol: "???",
    name: "Unknown Token",
    decimals: 18,
    logoUrl: "/jasmy-token.svg",
    color: "#9FFCE8",
    isNative: false,
    address,
  };
}

/* ─── ABI: DJNN Token (reward-related functions only) ────────────────────── */

export const DJNN_REWARD_ABI = [
  {
    type: "function",
    name: "rewardSlotCount",
    inputs: [],
    outputs: [{ name: "", type: "uint256" }],
    stateMutability: "view",
  },
  {
    type: "function",
    name: "distributors",
    inputs: [{ name: "slot", type: "uint256" }],
    outputs: [{ name: "", type: "address" }],
    stateMutability: "view",
  },
  {
    type: "function",
    name: "pendingReward",
    inputs: [
      { name: "holder", type: "address" },
      { name: "slot", type: "uint256" },
    ],
    outputs: [{ name: "", type: "uint256" }],
    stateMutability: "view",
  },
  {
    type: "function",
    name: "rewardBps",
    inputs: [{ name: "slot", type: "uint256" }],
    outputs: [{ name: "", type: "uint256" }],
    stateMutability: "view",
  },
  {
    type: "function",
    name: "rewardTokens",
    inputs: [{ name: "slot", type: "uint256" }],
    outputs: [{ name: "", type: "address" }],
    stateMutability: "view",
  },
  {
    type: "function",
    name: "balanceOf",
    inputs: [{ name: "account", type: "address" }],
    outputs: [{ name: "", type: "uint256" }],
    stateMutability: "view",
  },
] as const;

/* ─── ABI: ForgedDistributorV5 ───────────────────────────────────────────── */

export const DISTRIBUTOR_ABI = [
  {
    type: "function",
    name: "claimDividend",
    inputs: [{ name: "holder", type: "address" }],
    outputs: [],
    stateMutability: "nonpayable",
  },
  {
    type: "function",
    name: "unpaidEarnings",
    inputs: [{ name: "holder", type: "address" }],
    outputs: [{ name: "", type: "uint256" }],
    stateMutability: "view",
  },
  {
    type: "function",
    name: "getShareInfo",
    inputs: [{ name: "holder", type: "address" }],
    outputs: [
      { name: "amount", type: "uint256" },
      { name: "totalExcluded", type: "uint256" },
      { name: "totalRealised", type: "uint256" },
    ],
    stateMutability: "view",
  },
  {
    type: "function",
    name: "rewardToken",
    inputs: [],
    outputs: [{ name: "", type: "address" }],
    stateMutability: "view",
  },
  {
    type: "function",
    name: "totalDistributed",
    inputs: [],
    outputs: [{ name: "", type: "uint256" }],
    stateMutability: "view",
  },
  {
    type: "function",
    name: "totalDividends",
    inputs: [],
    outputs: [{ name: "", type: "uint256" }],
    stateMutability: "view",
  },
  {
    type: "function",
    name: "shareholderCount",
    inputs: [],
    outputs: [{ name: "", type: "uint256" }],
    stateMutability: "view",
  },
] as const;
